import type { TOC } from '@ember/component/template-only'
import { PageHeader } from 'teacher-ui-client/components/page-header'
import { RolloverHeaderReturnLink } from 'teacher-ui-client/components/rollover/header-return-link'
import { PageHeaderManageTabs } from 'teacher-ui-client/components/page-header/manage-tabs'
import { themeKey } from 'teacher-ui-client/helpers/theme-key'
import type ManageStudentsRoute from 'teacher-ui-client/manage/students/route'
import type ManageStudentsController from 'teacher-ui-client/manage/students/controller'
import { t } from 'ember-intl'
import { TilesManageActions } from 'teacher-ui-client/components/tiles/manage-actions'
import { usesExternalProvisioning } from 'teacher-ui-client/helpers/uses-external-provisioning'
import { FormCreateStudent } from 'teacher-ui-client/components/form/create-student'
import { usesExternalRostering } from 'teacher-ui-client/helpers/uses-external-rostering'
import { UiButtonLinkTo } from 'teacher-ui-client/components/ui-button/link-to'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import { on } from '@ember/modifier'
import { eq, and, not } from 'ember-truth-helpers'
import { DataOperationsTable } from 'teacher-ui-client/components/data-operations/table'
import { TabGroupComponent } from 'teacher-ui-client/components/tab-group'
import { TAB_TYPE_KEYS } from 'teacher-ui-client/components/tab-group/tab-types'
import { fn } from '@ember/helper'
import { uniqueId } from '@ember/helper'
import { FormComponentsFormSelect } from 'teacher-ui-client/components/form-components/form-select'
import { RadioGroup } from 'teacher-ui-client/components/base/radio-group'
import { usesExternalAuthentication } from 'teacher-ui-client/helpers/uses-external-authentication'
import { ManageStudentsMoreActionsComponent } from 'teacher-ui-client/components/manage/students/more-actions'
import { DownloadParentLetters } from 'teacher-ui-client/components/manage/students/download-parent-letters'
import { InputText } from 'teacher-ui-client/components/base/input-text'
import { pick } from 'teacher-ui-client/helpers/pick'
import { get } from '@ember/object'
import { includes } from 'teacher-ui-client/helpers/includes'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import { gradeName } from 'teacher-ui-client/helpers/grade-name'
import { array } from '@ember/helper'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { LayoutBlocksModal } from 'teacher-ui-client/components/layout-blocks/modal'
import { FormWrapperRemoveFromAllClassesForm } from 'teacher-ui-client/components/form-wrapper/remove-from-all-classes-form'
import { FormWrapperRemoveFromClassForm } from 'teacher-ui-client/components/form-wrapper/remove-from-class-form'
import { FormWrapperEditPasswordForm } from 'teacher-ui-client/components/form-wrapper/edit-password-form'
import { EditGradeForm } from 'teacher-ui-client/components/form-wrapper/edit-grade-form'
import { FormDeleteStudents } from 'teacher-ui-client/components/form/delete-students'
import { SortType } from 'teacher-ui-client/components/data-operations/table/sort-definition'
import RouteTemplate from 'ember-route-template'
import Component from '@glimmer/component'
import { service } from '@ember/service'
import type FeatureControlService from 'teacher-ui-client/services/feature-control'
import { OneRosterNotice } from 'teacher-ui-client/components/manage/one-roster/notice'
import config from 'teacher-ui-client/config/environment'
import { join } from 'teacher-ui-client/utils/uri'
import { featureFlag } from 'teacher-ui-client/helpers/feature-flag'
import { ColumnSortIcon } from 'teacher-ui-client/components/data-operations/table/column-sort-icon'

const oneRosterUrl = join(config.oneRosterUrl, 'login').toString().concat('?return_to=/manage-students')

interface Signature {
  Args: {
    model: ModelFor<ManageStudentsRoute>
    controller: ManageStudentsController
  }
}

interface OneRosterSignature {
  Args: {
    controller: ManageStudentsController
  }
}

export class ManageStudentsPage extends Component<Signature> {
  @service featureControl!: FeatureControlService

  get showOneRosterTemplate() {
    return this.featureControl.canAccess('one_roster')
  }

  <template>
    <PageHeader>
      <:title>
        <h1>
          {{t "manage.title.students"}}
        </h1>
      </:title>
      <:lowerLeft>
        <RolloverHeaderReturnLink />
      </:lowerLeft>
      <:lowerCenter>
        <PageHeaderManageTabs class="m-3" @themeKey={{themeKey @controller.currentSubscriptionType}} />
      </:lowerCenter>
    </PageHeader>
    {{#if this.showOneRosterTemplate}}
      <OneRosterManageStudents @controller={{@controller}} />
    {{else}}<ManageStudents @model={{@model}} @controller={{@controller}} />{{/if}}
  </template>
}

const OneRosterManageStudents: TOC<OneRosterSignature> = <template>
  <OneRosterNotice @linkUrl={{oneRosterUrl}} @linkText={{t "manage.3essentials.marketingTile.goToStudents"}} />
</template>

const ManageStudents: TOC<Signature> = <template>
  <TilesManageActions
    data-test-manage-actions-tile
    class="mb-6 p-4"
    @themeKey={{themeKey @controller.currentSubscriptionType}}
    @title={{t "manage.manageClasses.students.addStudents.title"}}
    as |tile|
  >
    {{#unless (usesExternalProvisioning)}}
      <tile.formArea class="max-w-3xl">
        <h5>
          {{t "manage.manageClasses.students.addStudents.form.title"}}
        </h5>
        <FormCreateStudent
          @schoolClassByTeacherOptions={{@controller.schoolClassByTeacherOptions}}
          @subscriptionType={{@controller.currentSubscriptionType}}
        />
      </tile.formArea>
    {{/unless}}
    <tile.actionsArea>
      {{#unless (usesExternalRostering)}}
        <UiButtonLinkTo data-test-move-students @route="manage.move-students">
          <span class="my-1 flex items-center">
            <PhosphorIcon class="mr-2 {{@controller.iconColorClass}} h-8 w-8" @icon="phosphor-user-plus-fill" />
            {{t "manage.manageClasses.students.moveStudents.button"}}
          </span>
        </UiButtonLinkTo>
        <span>
          <UiButtonLinkTo class="w-full" data-test-copy-classes-btn @route="subscription-type.copy-classes">
            <span class="my-1 flex items-center">
              <PhosphorIcon class="mr-2 {{@controller.iconColorClass}} h-8 w-8" @icon="phosphor-copy-fill" />
              {{t "components.copyClasses.link"}}
            </span>
          </UiButtonLinkTo>
          <Tooltip @text={{t "components.copyClasses.linkToolTip" program=(t @controller.currentSubscriptionType)}} />
        </span>
      {{/unless}}
      <UiButton data-text-export-all-students {{on "click" @controller.exportAllStudentData}}>
        <span class="my-1 flex items-center">
          <PhosphorIcon class="mr-2 {{@controller.iconColorClass}} h-8 w-8" @icon="phosphor-download-fill" />
          {{t "manage.manageClasses.students.exportDataButton"}}
        </span>
      </UiButton>
      {{#unless
        (and (eq @controller.currentSubscriptionType "writing") (not (featureFlag "tui_writing_certificates")))
      }}
        <UiButtonLinkTo
          data-test-print-certificates
          @disabled={{@controller.cannotPrintCertificates}}
          @route="manage.certificates"
        >
          <span class="my-1 flex items-center">
            <PhosphorIcon class="mr-2 {{@controller.iconColorClass}} h-8 w-8" @icon="phosphor-medal-fill" />
            {{t "manage.manageClasses.students.printCertificatesButton"}}
          </span>
        </UiButtonLinkTo>
      {{/unless}}
    </tile.actionsArea>
  </TilesManageActions>
  <DataOperationsTable @dataOperationsConfig={{@controller.dataOperationsConfig}} as |selectRecords sorting pagination|>
    {{#if @controller.isSubco}}
      <TabGroupComponent
        data-test-tab-group
        class="-mb-px"
        @theme={{themeKey @controller.currentSubscriptionType}}
        @tabType={{TAB_TYPE_KEYS.borderClassic}}
        as |tabGroup|
      >
        <tabGroup.tab
          data-test-tab-all-students
          @isActive={{eq @controller.currentTab "all_students"}}
          {{on "click" (fn @controller.changeTab "all_students")}}
        >
          {{t "manage.manageClasses.students.allStudentsTab"}}
        </tabGroup.tab>
        <tabGroup.tab
          data-test-tab-my-students
          @isActive={{eq @controller.currentTab "my_students"}}
          {{on "click" (fn @controller.changeTab "my_students")}}
        >
          {{t "manage.manageClasses.students.myStudentsTab"}}
        </tabGroup.tab>
      </TabGroupComponent>
    {{/if}}
    <div class="border-dusty-black-100 overflow-visible rounded-md rounded-tl-none border">
      <div class="{{@controller.tableTheme.controlsBg}} flex items-center p-5">
        {{#let (uniqueId) as |uniqueId|}}
          <label for="class-filter-{{uniqueId}}" class="mr-2 font-normal">
            {{t "manage.manageClasses.filterByGroupLabel"}}
          </label>
          <FormComponentsFormSelect
            id="class-filter-{{uniqueId}}"
            @optionClick={{@controller.setGroupFilterOption}}
            @optionTemplate={{@controller.groupFilterDropdownOptionTemplate}}
            @optionTemplateSubLevel={{@controller.groupFilterDropdownSubOptionTemplate}}
            @value={{@controller.groupFilterOption}}
            @staticBeforeOptions={{@controller.staticBeforeOptions}}
            @options={{@controller.schoolClassFilterOptions}}
            @resetAction={{@controller.resetGroupFilterOption}}
            @search={{@controller.isSubco}}
            @searchPlaceholder={{@controller.searchLabel}}
          />
        {{/let}}
        {{#if @controller.showAllocationFilter}}
          <div class="ml-auto flex items-center">
            <label class="mr-3">{{t "manage.manageClasses.filterByGroupLabel"}}</label>
            <RadioGroup
              @listClass="flex gap-2"
              @optionSet={{@controller.allocationOptionSet}}
              @checkedOptionId={{@controller.allocationFilterValue}}
              @onSelectionChange={{@controller.updateAllocationFilterValue}}
            />
          </div>
        {{/if}}
      </div>
      <div>
        <div class="{{@controller.tableTheme.controlsBg}} safari-gap-2 flex gap-2 p-5">
          {{#unless (usesExternalRostering)}}
            <span>
              <UiButton
                data-test-remove-selected-from-school-class
                disabled={{@controller.removeFromClassDisabled}}
                class="h-full"
                {{on "click" (fn @controller.setRemoveFromClassModal true)}}
              >
                {{t "manage.manageClasses.students.bulkActions.removeFromClass.title"}}
              </UiButton>
              <Tooltip @text={{@controller.removeFromClassesTooltipContent}} />
            </span>
          {{/unless}}
          {{#unless (usesExternalAuthentication)}}
            <UiButton
              data-test-edit-password-btn
              disabled={{selectRecords.noneSelected}}
              {{on "click" (fn @controller.showEditPasswordModal true)}}
            >
              {{t "manage.manageClasses.students.bulkActions.editPassword.title"}}
            </UiButton>
          {{/unless}}
          {{#unless (usesExternalRostering)}}
            <UiButton
              data-test-edit-grade-btn
              disabled={{selectRecords.noneSelected}}
              {{on "click" (fn @controller.showEditGradeModal true)}}
            >
              {{t "grade.edit"}}
            </UiButton>
          {{/unless}}
          <UiButton
            data-test-bulk-student-logins
            @disabled={{selectRecords.noneSelected}}
            {{on "click" (fn @controller.printStudentLogins selectRecords.selectedRecords)}}
          >
            {{t "manage.manageClasses.students.bulkActions.printLogins.title"}}
          </UiButton>
          <ManageStudentsMoreActionsComponent
            @disabled={{selectRecords.noneSelected}}
            @removeFromClassesModalAction={{fn @controller.showRemoveFromAllClassesModal true}}
            @exportStudentDataAction={{fn @controller.submitExportStudentData selectRecords.selectedRecords}}
            @deleteStudentsModalAction={{@controller.openDeleteStudentsModal}}
          />
          {{#unless (eq @controller.currentSubscriptionType "writing")}}
            <DownloadParentLetters @selectedIds={{@controller.selectedIds}} @disabled={{selectRecords.noneSelected}} />
          {{/unless}}
          <InputText
            class="ml-auto"
            type="search"
            role="searchbox"
            name="search-filter"
            value={{@controller.textFilter}}
            aria-label={{t "manage.manageTeachers.filterTeachers.label"}}
            placeholder={{t "manage.manageTeachers.filterTeachers.placeholder"}}
            @themeKey={{themeKey @controller.currentSubscriptionType}}
            @icon="search"
            @iconEnd={{true}}
            {{on "input" (pick "target.value" @controller.setTextFilter)}}
          />
        </div>
        <table data-test-manage-students-table class="w-full border-t border-white">
          <thead class={{@controller.tableTheme.headerBg}}>
            <tr class="divide-x divide-white border-b border-white">
              <th class="w-14 p-3 text-center">
                <label class="cursor-pointer">
                  {{t "manage.selectAllLabel"}}
                  <input
                    data-test-select-all-records
                    type="checkbox"
                    class="cursor-pointer"
                    checked={{selectRecords.allSelected}}
                    {{on "change" selectRecords.toggleAll}}
                  />
                </label>
              </th>
              <th class="max-w-32">
                <button
                  type="button"
                  class="w-full px-3 py-6 text-left whitespace-nowrap"
                  {{on "click" (fn sorting.sort SortType.ByValue "firstName")}}
                >
                  {{t "manage.firstName"}}
                  <ColumnSortIcon @columnName="firstName" @sortCol={{sorting.sortCol}} @sortDir={{sorting.sortDir}} />

                </button>
              </th>
              <th class="max-w-32">
                <button
                  type="button"
                  class="w-full px-3 py-6 text-left whitespace-nowrap"
                  {{on "click" (fn sorting.sort SortType.ByValue "lastName")}}
                >
                  {{t "manage.lastName"}}
                  <ColumnSortIcon @columnName="lastName" @sortCol={{sorting.sortCol}} @sortDir={{sorting.sortDir}} />
                </button>
              </th>
              {{#unless (usesExternalAuthentication)}}
                <th data-test-login-header>
                  <button
                    type="button"
                    class="w-full px-3 py-6 text-left whitespace-nowrap"
                    {{on "click" (fn sorting.sort SortType.ByValue "login")}}
                  >
                    {{t "manage.login"}}
                    <ColumnSortIcon @columnName="login" @sortCol={{sorting.sortCol}} @sortDir={{sorting.sortDir}} />
                  </button>
                </th>
              {{/unless}}
              <th>
                <button
                  type="button"
                  class="w-full p-3 text-left"
                  {{on "click" (fn sorting.sort SortType.ByValue @controller.joinedSchoolClassNamesKey)}}
                >
                  {{t "manage.class"}}
                  <ColumnSortIcon
                    @columnName={{@controller.joinedSchoolClassNamesKey}}
                    @sortCol={{sorting.sortCol}}
                    @sortDir={{sorting.sortDir}}
                  />
                </button>
              </th>
              <th>
                <button
                  type="button"
                  class="w-full px-3 py-6 text-left whitespace-nowrap"
                  {{on "click" (fn sorting.sort SortType.ByNumber "gradePosition")}}
                >
                  {{t "grade.label"}}
                  <ColumnSortIcon
                    @columnName="gradePosition"
                    @sortCol={{sorting.sortCol}}
                    @sortDir={{sorting.sortDir}}
                  />
                </button>
              </th>
              <th aria-label={{t "manage.edit"}}></th>
            </tr>
          </thead>
          <tbody>
            {{#each pagination.currentPageRecords as |row|}}
              {{#let (eq @controller.editingRecordId row.id) as |isEditing|}}
                <tr
                  data-test-record-row
                  class={{if isEditing @controller.tableTheme.bodyRowBgEditing @controller.tableTheme.bodyRowBg}}
                >
                  <td class="w-14 p-3 text-center">
                    <input
                      data-test-select-record
                      type="checkbox"
                      class="cursor-pointer"
                      checked={{includes selectRecords.selectedIds row.id}}
                      aria-label={{t "manage.manageClasses.selectSingleRecordLabel" name=row.fullName}}
                      {{on "change" (fn selectRecords.toggle row.id)}}
                    />
                  </td>
                  <td class="max-w-32 p-3 wrap-break-word {{if isEditing 'font-bold'}}">
                    {{row.firstName}}
                  </td>
                  <td class="max-w-32 p-3 wrap-break-word {{if isEditing 'font-bold'}}">
                    {{row.lastName}}
                  </td>
                  {{#unless (usesExternalAuthentication)}}
                    <td class="p-3 {{if isEditing 'font-bold'}} break-all">{{row.login}}</td>
                  {{/unless}}
                  <td data-test-classes-cell class="p-3 {{if isEditing 'font-bold'}}">
                    {{formatValue
                      (get row @controller.joinedSchoolClassNamesKey)
                      replacement=(t "manage.noClassAssigned")
                    }}
                  </td>
                  <td class="p-3 {{if isEditing 'font-bold'}}">
                    {{gradeName row.gradePosition}}
                  </td>
                  <td class="p-3">
                    {{#if isEditing}}
                      <UiButtonLinkTo
                        data-test-edit-student-close
                        class="font-normal whitespace-nowrap"
                        @route="manage.students"
                        @appearance={{ButtonAppearance.muted}}
                      >
                        <PhosphorIcon @icon="phosphor-x-bold" class="mr-2" />
                        {{t "manage.close"}}
                      </UiButtonLinkTo>
                    {{else}}
                      <UiButtonLinkTo
                        data-test-edit-student-open
                        class="font-normal whitespace-nowrap"
                        @route="manage.students.student"
                        @models={{array row.id}}
                      >
                        <PhosphorIcon @icon="phosphor-note-pencil-fill" class="mr-2" />
                        {{t "manage.edit"}}
                      </UiButtonLinkTo>
                    {{/if}}
                  </td>
                </tr>
                {{#if isEditing}}
                  <tr>
                    <td colspan={{if (usesExternalAuthentication) "6" "7"}} class="bg-juicy-orange-50 p-3">
                      {{outlet}}
                    </td>
                  </tr>
                {{/if}}
              {{/let}}
            {{else}}
              <tr>
                <td colspan={{if (usesExternalAuthentication) "6" "7"}} class="p-3 text-center italic">
                  {{t "manage.noDataAvailable"}}
                </td>
              </tr>
            {{/each}}
          </tbody>
        </table>
      </div>
    </div>
    <LayoutBlocksModal
      @title={{t "manage.manageClasses.students.bulkActions.removeFromClass.title"}}
      @showModal={{@controller.removeFromClassModalOpen}}
      @close={{fn @controller.setRemoveFromClassModal false}}
    >
      <FormWrapperRemoveFromClassForm
        @subscriptionType={{@controller.currentSubscriptionType}}
        @students={{selectRecords.selectedRecords}}
        @onSubmit={{@controller.submitRemoveFromClassModal}}
        @onCancel={{fn @controller.setRemoveFromClassModal false}}
      />
    </LayoutBlocksModal>
    <LayoutBlocksModal
      @title={{t "manage.manageClasses.students.bulkActions.editPassword.title"}}
      @showModal={{@controller.editPasswordModalOpen}}
      @close={{fn @controller.showEditPasswordModal false}}
    >
      <FormWrapperEditPasswordForm
        @students={{selectRecords.selectedRecords}}
        @setMultiplePasswordsTask={{@controller.setMultiplePasswordsTask}}
        @onCancel={{fn @controller.showEditPasswordModal false}}
      />
    </LayoutBlocksModal>
    <LayoutBlocksModal
      @title={{t "grade.edit"}}
      @showModal={{@controller.editGradeModalOpen}}
      @close={{fn @controller.showEditGradeModal false}}
    >
      <EditGradeForm
        @students={{selectRecords.selectedRecords}}
        @setMultipleGradesTask={{@controller.setMultipleGradesTask}}
        @onCancel={{fn @controller.showEditGradeModal false}}
      />
    </LayoutBlocksModal>
    <LayoutBlocksModal
      @title={{t "manage.manageClasses.students.bulkActions.removeFromAllClasses.title"}}
      @showModal={{@controller.removeFromAllClassesModalOpen}}
      @close={{fn @controller.showRemoveFromAllClassesModal false}}
    >
      <FormWrapperRemoveFromAllClassesForm
        @subscriptionType={{@controller.currentSubscriptionType}}
        @students={{selectRecords.selectedRecords}}
        @onSubmit={{@controller.submitRemoveFromAllClassesModal}}
        @onCancel={{fn @controller.showRemoveFromAllClassesModal false}}
      />
    </LayoutBlocksModal>
    <LayoutBlocksModal
      @title={{t "manage.manageClasses.students.bulkActions.deleteStudents.title" count=@controller.selectedIds.length}}
      @showModal={{@controller.deleteStudentsModalOpen}}
      @close={{@controller.closeDeleteStudentsModal}}
    >
      <FormDeleteStudents
        data-test-delete-students-form
        @themeKey={{themeKey @controller.currentSubscriptionType}}
        @students={{selectRecords.selectedRecords}}
        @onSuccess={{@controller.onDeleteStudentsSuccess}}
        @closeModalAction={{@controller.closeDeleteStudentsModal}}
      />
    </LayoutBlocksModal>
  </DataOperationsTable>
</template>

export default RouteTemplate(ManageStudentsPage)
